import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Card, CardContent } from '../ui';
import { BarChart3, TrendingUp, ShoppingCart, Calendar } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';

/**
 * AnalyticsSummary Component
 * 
 * Displays key analytics metrics:
 * - Total items count
 * - Average profit per item
 * - Best performing platform
 * - This month's new items count
 */
const AnalyticsSummary = React.memo(function AnalyticsSummary({ items }) {
  const analytics = useMemo(() => {
    if (!items || items.length === 0) {
      return {
        totalItems: 0,
        averageProfit: 0,
        bestPlatform: '-',
        thisMonthItems: 0
      };
    }

    // Total items
    const totalItems = items.length;

    // Average profit per item (only for items with sale price)
    const itemsWithProfit = items.filter(item => 
      item.sale_price_gel && item.purchase_price_usd
    );
    
    const totalProfit = itemsWithProfit.reduce((sum, item) => {
      const profit = item.sale_price_gel - (item.purchase_price_usd * 2.75);
      return sum + profit;
    }, 0);
    
    const averageProfit = itemsWithProfit.length > 0 
      ? totalProfit / itemsWithProfit.length 
      : 0;

    // Best performing platform
    const platformCounts = items.reduce((acc, item) => {
      acc[item.platform] = (acc[item.platform] || 0) + 1;
      return acc;
    }, {});
    
    const bestPlatform = Object.entries(platformCounts)
      .sort((a, b) => b[1] - a[1])[0]?.[0] || '-';

    // This month's new items
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    const thisMonthItems = items.filter(item => {
      if (!item.order_date) return false;
      const itemDate = new Date(item.order_date);
      return itemDate.getMonth() === currentMonth && 
             itemDate.getFullYear() === currentYear;
    }).length;

    return {
      totalItems,
      averageProfit,
      bestPlatform,
      thisMonthItems
    };
  }, [items]);

  const statCards = [
    {
      title: 'სულ ნივთები',
      value: analytics.totalItems,
      icon: ShoppingCart,
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: 'საშუალო მოგება',
      value: formatCurrency(analytics.averageProfit),
      icon: TrendingUp,
      color: 'from-emerald-500 to-green-600'
    },
    {
      title: 'საუკეთესო პლატფორმა',
      value: analytics.bestPlatform,
      icon: BarChart3,
      color: 'from-purple-500 to-purple-600'
    },
    {
      title: 'ამ თვის ნივთები',
      value: analytics.thisMonthItems,
      icon: Calendar,
      color: 'from-orange-500 to-amber-600'
    }
  ];

  return (
    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4 mb-6 stagger-children">
      {statCards.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <Card 
            key={index} 
            className="overflow-hidden border-0 shadow-md hover:shadow-lg transition-shadow duration-200"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-md`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground font-medium">
                    {stat.title}
                  </p>
                  <p className="text-xl font-bold text-foreground mt-0.5">
                    {stat.value}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
});

AnalyticsSummary.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      sale_price_gel: PropTypes.number,
      purchase_price_usd: PropTypes.number,
      platform: PropTypes.string.isRequired,
      order_date: PropTypes.string
    })
  )
};

export default AnalyticsSummary;
